import { useEffect, useEffectEvent, useState } from 'react';

import {
  failConnectionAddOperation,
  failConnectionReconnectOperation,
  startConnectionAddOperation,
  startConnectionReconnectOperation,
  succeedConnectionReconnectOperation,
} from '@mint-lib/connections-ui';
import type {
  AuthorizeResult,
  NormalizedConnection,
  OAuthFlow,
} from '@mint-lib/integration-registry';
import { useQuery } from '@mint-lib/query-client';
import { AdFormIntegrations } from '@mint-sdk/api-integrations';
import { Integrations } from '@mint-sdk/api-omni-trafficking';

import { ADFORM_URN } from './repository.js';

/**
 * "Does this callback belong to AdForm?" — ported from
 * `traffickingOAuthCallbackStrategies.ts`'s `adFormOauthStrategy.isEnabled`.
 * AdForm never uses the generic `oauth_result` search param (that belongs to
 * platforms whose callback is handled server-side, e.g. Amazon/TradeDesk) —
 * AdForm's own round trip is entirely `state`/`code`, with the connection id
 * and prospective name encoded into `state` itself.
 */
export function canProcess(search: Record<string, unknown>): boolean {
  const state = typeof search.state === 'string' ? search.state : undefined;
  const code = typeof search.code === 'string' ? search.code : undefined;

  return Boolean(
    state &&
    code &&
    AdFormIntegrations.parseAdFormOAuthState(state)?.integrationName,
  );
}

/**
 * Kicks off (add) or resumes (manage/reconnect) the AdForm authorize
 * redirect. Ported from `traffickingReconnectStrategies.ts`'s
 * `adFormAuthorizeStrategy.useAuthorize`. Unlike CM/GA4, AdForm never writes
 * to `LocalStorage` before redirecting — the connection id + prospective
 * name travel in the `state` param itself (see `oauth_result` note above),
 * so no loader-level `resumeRedirect` is needed: the whole flow round-trips
 * back through the mounted add form, which decodes `state` itself via
 * `useAdFormOAuthCallback`.
 */
export function useAuthorize({
  connection,
}: {
  connection?: NormalizedConnection;
}): AuthorizeResult {
  const [enabled, setEnabled] = useState(false);
  const { data, error, isLoading, refetch } = useQuery({
    // TODO(adformBackendOwnedAuth): switch to the BE-owned flow.
    ...Integrations.getAdformOAuthUrlLegacy({ params: {} }),
    enabled,
  });

  useEffect(() => {
    if (!data) return;

    const { scope, state, responseType, clientId, redirectUri } = data;
    const encodedStatePayload =
      AdFormIntegrations.encodeAdFormOAuthStatePayload({
        connectionId: connection?.id ?? '',
        integrationName: connection?.name ?? '',
      });
    const authorizeUrl = `https://id.adform.com/sts/connect/authorize?scope=${scope}&state=${state}.${encodedStatePayload}&response_type=${responseType}&client_id=${clientId}&redirect_uri=${redirectUri}`;

    globalThis.location.assign(authorizeUrl);
  }, [connection?.id, connection?.name, data]);

  useEffect(() => {
    if (!error) return;
    failConnectionAddOperation('error');
    failConnectionReconnectOperation('error');
  }, [error]);

  return {
    authorize: () => {
      // The add form passes a draft connection with an empty id (it only
      // carries the typed name); a real id means a reconnect.
      const context = {
        platform: ADFORM_URN,
        flow: 'oauth',
        registry: true,
      } as const;
      if (connection?.id) {
        startConnectionReconnectOperation(context);
      } else {
        startConnectionAddOperation(context);
      }
      setEnabled(true);
      void refetch();
    },
    isPending: enabled && isLoading,
  };
}

/** `OAuthFlow` implementation registered on AdForm's `IntegrationDefinition`. */
export const adFormOAuthFlow: OAuthFlow = {
  canProcess,
  useAuthorize,
};

export interface AdFormOAuthCallbackResult {
  /** Set once `postAdformOAuthCallback` resolves — gates the advertiser-select step. */
  oauthFlowId: string | undefined;
  /** Decoded straight out of `state` — the name typed on the add form before redirecting out. */
  connectionName: string | undefined;
  /** Non-empty only when this round trip was a reconnect of an existing connection. */
  reconnectConnectionId: string | undefined;
  isLoading: boolean;
}

function readSearchParam(name: string): string | undefined {
  if (globalThis.location === undefined) return undefined;
  return new URLSearchParams(globalThis.location.search).get(name) ?? undefined;
}

/**
 * Processes an in-flight AdForm OAuth callback. `AddFormProps`/`ManageFormProps`
 * carry no route search params, so — like the legacy
 * `useTraffickingSearchParamsHandler` — this reads `state`/`code` directly off
 * the browser URL rather than through a router dependency this package can't
 * take (routing stays owned by `packages/connections`/`apps/routing`).
 * Ported from `traffickingOAuthCallbackStrategies.ts`'s `adFormOauthStrategy.useQuery`.
 */
export function useAdFormOAuthCallback({
  onReconnected,
}: {
  /**
   * Called once the code exchange refreshed the existing connection of a
   * reconnect. AdForm's `redirect_uri` is the add route, so the add form has
   * to send the user back to that connection itself.
   */
  onReconnected?: (connectionId: string) => void;
} = {}): AdFormOAuthCallbackResult {
  const state = readSearchParam('state');
  const code = readSearchParam('code');
  const parsedState = AdFormIntegrations.parseAdFormOAuthState(state);
  const integrationName = parsedState?.integrationName;
  const connectionId = parsedState?.connectionId;

  const { mutate, data, isPending } = Integrations.postAdformOAuthCallback();
  const handleReconnected = useEffectEvent((id: string) => {
    onReconnected?.(id);
  });

  useEffect(() => {
    if (!state || !code || !integrationName) return;

    mutate(
      {
        data: {
          name: integrationName,
          code,
          integrationId: connectionId || null,
        },
      },
      {
        // A reconnect (the state carries a connection id) is done once the
        // code exchange refreshed the existing integration's token; nothing
        // on the add form that follows is part of it.
        onSuccess: () => {
          if (!connectionId) return;
          succeedConnectionReconnectOperation();
          handleReconnected(connectionId);
        },
        onError: () => {
          failConnectionAddOperation('error');
          failConnectionReconnectOperation('error');
        },
      },
    );
  }, [state, code, integrationName, connectionId, mutate]);

  return {
    oauthFlowId: data,
    connectionName: integrationName,
    reconnectConnectionId: connectionId || undefined,
    isLoading: isPending,
  };
}
